/**
 * Zone Grid Component
 *
 * Displays a grid of zones with worker assignments
 */

import React from 'react'
import { useNavigate } from 'react-router-dom'
import type { TaskType, Worker, WorkerAssignmentLog } from 'src/store/todo/types'
import type { GridLayout, WorkerZoneAssignment } from '../../types'
import WorkerBadge from './worker-badge'
import Fallback from 'src/components/shared/fallback'

type ZoneGridProps = {
  /** Grid layout with 2D array of zones */
  gridLayout: GridLayout
  /** Workers grouped by zone ID */
  workersByZone: Record<string, WorkerZoneAssignment[]>
  /** Map of worker IDs to Worker objects */
  workersMap: Record<string, Worker>
  /** Array of all task types */
  tasks: TaskType[]
  /** Map of task IDs to TaskType objects */
  tasksMap: Record<string, TaskType>
  /** Array of all worker assignment logs */
  logs: WorkerAssignmentLog[]
  /** Current team ID */
  teamId: string
}

/**
 * ZoneGrid component displaying the zone-based worker visualization
 *
 * @example
 * <ZoneGrid
 *   gridLayout={layout}
 *   workersByZone={workersByZone}
 *   workersMap={workersMap}
 *   tasks={tasks}
 *   tasksMap={tasksMap}
 *   logs={logs}
 *   teamId={teamId}
 * />
 */
const ZoneGrid: React.FC<ZoneGridProps> = ({
  gridLayout,
  workersByZone,
  workersMap,
  tasks,
  tasksMap,
  logs,
  teamId,
}) => {
  const navigate = useNavigate()
  const { grid, rowIndices } = gridLayout

  if (grid.length === 0) {
    return <Fallback title="No zones configured for this greenhouse" />
  }

  return (
    <div className="overflow-x-auto -mx-4 px-4">
      <table className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
        <tbody>
          {rowIndices.map((rowIdx) => (
            <tr key={rowIdx} className="h-auto">
              {grid.map((column, colIdx) => {
                const zone = column[rowIdx]
                const workersInZone = zone ? workersByZone[zone.id] || [] : []

                return (
                  <td
                    key={`${colIdx}-${rowIdx}`}
                    className="border border-gray-300 px-2 py-1 bg-white relative align-top min-h-[48px]"
                  >
                    {zone ? (
                      <>
                        {/* Zone Moniker - Background Text */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20">
                          <span className="text-lg font-semibold text-gray-400">
                            {zone.zoneMoniker}
                          </span>
                        </div>

                        {/* Worker Initials Grid */}
                        <div className="relative py-0">
                          <div
                            className={`flex flex-wrap items-center gap-1 leading-none min-h-[28px] ${
                              workersInZone.length === 0
                                ? 'justify-center text-gray-300 text-[10px]'
                                : ''
                            }`}
                          >
                            {workersInZone.length > 0 ? (
                              workersInZone.map((assignment) => {
                                const worker = workersMap[assignment.workerId]
                                if (!worker) return null

                                return (
                                  <WorkerBadge
                                    key={`${assignment.workerId}-${assignment.taskTypeId}-${zone.id}`}
                                    firstName={worker.firstName}
                                    lastName={worker.lastName}
                                    assignment={assignment}
                                    zoneId={zone.id}
                                    tasks={tasks}
                                    tasksMap={tasksMap}
                                    logs={logs}
                                    onClick={() =>
                                      navigate(
                                        `/analytics/zone/${zone.id}?workerId=${assignment.workerId}&taskTypeId=${assignment.taskTypeId}&teamId=${teamId}`
                                      )
                                    }
                                  />
                                )
                              })
                            ) : (
                              <span className="w-full text-center">—</span>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="h-full" />
                    )}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default ZoneGrid
